import React, { useContext } from "react";
import { Form, Input, Select } from "antd";
import { countries } from "@/constants/countries";
import { usercontext } from "@/lib/context";
import styles from "./CustomPhoneInput.module.css";

export const setPhoneInputInitialValue = (phone) => {
  if (!phone) return { phone: "", country_code: "IN" };
  const rawPhone = String(phone).replace("+", "");
  const sortedCountries = [...countries].sort(
    (a, b) => b.phoneCode.length - a.phoneCode.length,
  );
  const matchedCountry = sortedCountries.find((c) =>
    rawPhone.startsWith(c.phoneCode.replace("+", "")),
  );

  if (matchedCountry) {
    const codeLen = matchedCountry.phoneCode.replace("+", "").length;
    return {
      phone: rawPhone.slice(codeLen),
      country_code: matchedCountry.code,
    };
  }

  return {
    phone: rawPhone.slice(2),
    country_code: "IN",
  };
};

export const countryCodeToPhoneCode = Object.fromEntries(
  countries.map((c) => [c.code, c.phoneCode]),
);

export const countryCodeToPhoneDigits = Object.fromEntries(
  countries.map((c) => [
    c.code,
    { min: c.minPhoneDigits, max: c.maxPhoneDigits },
  ]),
);
export const DEFAULT_PHONE_DIGITS = { min: 6, max: 12 };

export const isPhoneNumberValid = (countryCode, value) => {
  if (!value || !/^\d+$/.test(value)) return false;
  const { min, max } =
    countryCodeToPhoneDigits[countryCode] || DEFAULT_PHONE_DIGITS;
  return value.length >= min && value.length <= max;
};

export const buildPhoneContactInfo = (country_code, phone) => {
  const dialCode = countryCodeToPhoneCode[country_code] || "";
  return {
    countryCode: dialCode.replace(/\D/g, ""),
    areaCode: "",
    phoneNumber: String(phone ?? "").replace("undefined", ""),
    isoCode: country_code || "",
  };
};

// Flat "<dial code><digits>" string, e.g. "919876543210" — dial code, never the ISO code.
export const buildFullPhone = (country_code, phone) => {
  const dialCode = countryCodeToPhoneCode[country_code] || "";
  return `${dialCode.replace(/\D/g, "")}${String(phone ?? "").replace("undefined", "")}`;
};

// Use unique country code ("CA", "US") as value so duplicate phone codes
// (e.g. Canada & USA both +1) don't cause the wrong flag to display.
const options = countries.map((country) => ({
  value: country.code,
  label: (
    <div
      style={{ display: "flex", alignItems: "center", gap: "10px" }}
      className="text-[16px]"
    >
      <img
        src={`https://flagcdn.com/${country.code.toLowerCase()}.svg`}
        alt={country.name}
        style={{ width: "25px", objectFit: "contain" }}
      />
      {country.phoneCode}
    </div>
  ),
  searchValue: `${country.name} ${country.phoneCode}`,
}));

const CustomPhoneInput = ({
  disabled,
  phoneRules = [],
  placeholder = "Phone number",
  // Fixed dropdown width (px) for narrow selects; defaults to the select's width.
  popupWidth,
}) => {
  const { isoCode } = useContext(usercontext);
  const form = Form.useFormInstance();
  return (
    <>
      <div
        style={{ display: "flex", gap: "8px" }}
        className="mt-[5px] phone-input-row"
      >
        <Form.Item
          name="country_code"
          initialValue={isoCode || "IN"}
          style={{ marginBottom: "5px" }}
        >
          <Select
            showSearch
            placeholder="Select country"
            style={{ width: "110px" }}
            popupMatchSelectWidth={popupWidth ?? true}
            className="!text-[16px] country-code"
            disabled={disabled}
            onChange={() => {
              if (form) {
                form.validateFields(["phone"]).catch(() => {});
              }
            }}
            filterOption={(input, option) =>
              (option?.searchValue ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={options}
          />
        </Form.Item>

        <Form.Item
          shouldUpdate={(prevValues, currentValues) =>
            prevValues?.country_code !== currentValues?.country_code
          }
          noStyle
        >
          {() => {
            const currentCountry =
              form?.getFieldValue("country_code") || isoCode || "IN";
            const { min: minLen, max: maxLen } =
              countryCodeToPhoneDigits[currentCountry] || DEFAULT_PHONE_DIGITS;

            return (
              <Form.Item
                name="phone"
                style={{ marginBottom: "5px" }}
                className={`!w-full ${styles.phoneFormItem}`}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject("Phone number is required");
                      }
                      if (!/^\d+$/.test(value)) {
                        return Promise.reject(
                          "Phone number must contain only digits",
                        );
                      }
                      if (!isPhoneNumberValid(currentCountry, value)) {
                        return Promise.reject(
                          minLen === maxLen
                            ? `Phone number must be exactly ${minLen} digits`
                            : `Phone number must be between ${minLen} and ${maxLen} digits`,
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                  ...phoneRules,
                ]}
              >
                <Input
                  className=" !px-[12px] !text-[16px]"
                  autoComplete="tel"
                  disabled={disabled}
                  placeholder={placeholder || "Phone number"}
                  maxLength={maxLen}
                  style={{ flex: 1 }}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, "");
                  }}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </div>
      <Form.Item shouldUpdate noStyle>
        {() => {
          const errors = form?.getFieldError("phone") || [];
          return errors.length ? (
            <div className={styles.phoneError}>{errors[0]}</div>
          ) : null;
        }}
      </Form.Item>
    </>
  );
};

export default CustomPhoneInput;
